/**
 * Admin -> Settings -> Integrations: Google Sheets nusxasi. Platformadagi hozirgi holat asl jadval ko'rinishida
 * (Partners, har filial Database, Pipeline): .xlsx yuklab olish va Google jadvalga avtomatik yozish (bir tomonga).
 * Jadval service account emailiga Editor qilib ulashiladi; havola saqlanishidan oldin backend yozib ko'radi.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Download, ExternalLink, Link2, RefreshCw, Unlink } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, formatDateTime, formatWhen } from "@/lib/utils";
import { sheetsDownloadUrl, useSheetsStatus, useSyncSheets, useUpdateSheets, type SheetsStatus } from "@/features/integrations/api";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { confirmDialog } from "@/components/ui/confirm";
import { Input } from "@/components/ui/field";
import { ErrorState, Spinner } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { Toggle } from "@/components/ui/toggle";
import { CopyField } from "./IntegrationControls";

type PillKey = "on" | "paused" | "error" | "off";

function pillFor(s: SheetsStatus): { key: PillKey; cls: string } {
  if (s.spreadsheet_id && s.last_error) return { key: "error", cls: "bg-status-rejected text-ink" };
  if (s.spreadsheet_id && s.enabled) return { key: "on", cls: "bg-status-hired text-ink" };
  if (s.spreadsheet_id) return { key: "paused", cls: "bg-fill text-ink" };
  return { key: "off", cls: "bg-fill text-ink" };
}

export function SheetsCard() {
  const { t } = useTranslation("settings");
  const status = useSheetsStatus();

  if (status.isPending) {
    return (
      <Card>
        <CardBody className="flex justify-center py-6">
          <Spinner />
        </CardBody>
      </Card>
    );
  }
  if (status.isError) return <ErrorState message={status.error.message} onRetry={() => status.refetch()} />;
  const s = status.data;
  const pill = pillFor(s);

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {t("sheets.title")} <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", pill.cls)}>{t(`sheets.pill.${pill.key}`)}</span>
          </span>
        }
        subtitle={t("sheets.subtitle")}
        action={
          <a
            href={sheetsDownloadUrl}
            className="inline-flex h-8 shrink-0 items-center gap-2 rounded-[10px] border border-border bg-surface px-3 text-[13px] font-medium text-ink transition-colors hover:bg-fill/60"
          >
            <Download size={14} /> {t("sheets.download")}
          </a>
        }
      />
      <CardBody className="space-y-4">
        {!s.configured ? (
          <p className="text-[13px] text-ink-soft">{t("sheets.notConfiguredDesc")}</p>
        ) : s.spreadsheet_id ? (
          <Connected s={s} />
        ) : (
          <Connect s={s} />
        )}
        <p className="text-[12px] text-muted">{t("sheets.oneWay")}</p>
      </CardBody>
    </Card>
  );
}

/** Ulanmagan: 1) jadval oching, 2) shu emailga ulashing, 3) havolani qo'ying */
function Connect({ s }: { s: SheetsStatus }) {
  const { t } = useTranslation("settings");
  const update = useUpdateSheets();
  const [link, setLink] = useState("");
  const [error, setError] = useState("");

  async function connect() {
    setError("");
    try {
      await update.mutateAsync({ spreadsheet: link.trim(), enabled: true, version: s.version });
      toast.success(t("sheets.connected"));
      setLink("");
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) toast.error(t("integrations.conflict"));
      else setError(err instanceof ApiError ? err.message : t("integrations.saveFailed"));
    }
  }

  return (
    <ol className="list-decimal space-y-3 pl-5 text-[13px]">
      <li>{t("sheets.step1")}</li>
      <li>
        <div className="mb-1">{t("sheets.step2")}</div>
        <CopyField value={s.service_account_email} />
      </li>
      <li>
        <div className="mb-1">{t("sheets.step3")}</div>
        <div className="flex gap-2">
          <Input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && link.trim() && connect()}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            aria-invalid={!!error}
            aria-label={t("sheets.link")}
          />
          <Button variant="primary" disabled={!link.trim()} loading={update.isPending} onClick={connect}>
            <Link2 size={14} /> {t("sheets.connect")}
          </Button>
        </div>
        {update.isPending && <div className="mt-1 text-[12px] text-muted">{t("sheets.firstWrite")}</div>}
        {error && <div className="mt-1 text-[12px] text-danger">{error}</div>}
      </li>
    </ol>
  );
}

function Connected({ s }: { s: SheetsStatus }) {
  const { t } = useTranslation("settings");
  const update = useUpdateSheets();
  const sync = useSyncSheets();

  async function patch(data: { spreadsheet?: string; enabled?: boolean }, done?: string) {
    try {
      await update.mutateAsync({ ...data, version: s.version });
      if (done) toast.success(done);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) toast.error(t("integrations.conflict"));
      else toast.error(err instanceof ApiError ? err.message : t("integrations.saveFailed"));
    }
  }

  async function syncNow() {
    try {
      await sync.mutateAsync();
      toast.success(t("sheets.synced"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("integrations.requestFailed"));
    }
  }

  async function disconnect() {
    const ok = await confirmDialog({
      title: t("sheets.disconnectTitle"),
      description: t("sheets.disconnectDesc"),
      confirmLabel: t("sheets.disconnect"),
      danger: true,
    });
    if (ok) await patch({ spreadsheet: "", enabled: false }, t("sheets.disconnected"));
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line px-3 py-2.5">
        <div className="min-w-0">
          <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline">
            {s.title || t("sheets.untitled")} <ExternalLink size={13} />
          </a>
          <div className="text-[12px] text-muted">
            {s.last_synced_at ? t("sheets.updated", { when: formatWhen(s.last_synced_at) }) : t("sheets.notYet")}
            {s.updated_by && ` · ${t("sheets.connectedBy", { who: s.updated_by })}`}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" loading={sync.isPending} onClick={syncNow}>
            <RefreshCw size={14} /> {t("sheets.syncNow")}
          </Button>
          <Button size="sm" variant="danger" onClick={disconnect}>
            <Unlink size={14} /> {t("sheets.disconnect")}
          </Button>
        </div>
      </div>

      {s.last_error && (
        <div className="flex gap-2 rounded-[10px] border border-danger bg-danger-soft px-3 py-2.5 text-[13px]">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger" />
          <div>
            <div className="font-semibold text-danger">{t("sheets.errorTitle")}</div>
            <div className="text-ink-soft">{s.last_error}</div>
            <div className="mt-1 text-[11px] text-muted">{formatDateTime(s.last_error_at)}</div>
          </div>
        </div>
      )}

      <Toggle label={t("sheets.auto")} hint={t("sheets.autoHint")} checked={s.enabled} onChange={(v) => patch({ enabled: v })} />

      {s.last_stats.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted">
          {s.last_stats.map((tab) => (
            <span key={tab.title}>
              {tab.title}: <span className="font-semibold text-ink-soft">{t("sheets.rows", { count: tab.rows })}</span>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
